import React, { useState, useRef, useCallback, useLayoutEffect } from 'react';
import { Keyboard, Alert, FlatList } from 'react-native';
import { emojify } from 'node-emoji';
import Button, { HEIGHT } from 'design-system/Button';
import { rem, CardView, AppView, SettingsItemInput, SettingsItemNavigate, withAnimatedNavigationHeader, } from 'design-system';
import EmojiPickerModal from 'design-system/emojiPicker/EmojiPickerModal';
import EmojiText from 'design-system/EmojiText';
import { IconEmma, IconText, IconTrash } from 'design-system/icons';
import { AnimatedKeyboardAwareScrollView } from 'design-system/AnimatedComponents';
import NavigationHeaderRightIconButton from 'design-system/NavigationHeaderRightIconButton';
import { extractKeyFromKey } from 'utils/flatListHelpers';
import { AddToWatchListFlow } from 'utils/types/navigation';
import createStyleSheets from 'utils/createStyleSheets';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useAppRating from 'hooks/useAppRating';
import { useWatchlistCache, useWatchListAssetsCache } from '../hooks/selectors';
import useWatchList from '../hooks/useWatchList';
import WatchlistSuggestions from '../components/WatchlistSuggestions';
import { AssetStateCache } from '../types';
import { AddToWatchListModal } from '../components/AddToWatchListModal';
import EditWatchListStockItem from '../components/EditWatchListStockItem';
import SectionHeader from '../components/SectionHeader';
import { isWeb } from '../../../constants';
const CreateWatchListScreen = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const styles = useStyles(styleSet);
    const rateApp = useAppRating();
    const createParams = route.params?.createParams;
    const id = route.params?.id;
    const { flow } = createParams;
    const isEditFlow = flow === AddToWatchListFlow.UpdateWatchList;
    const colors = useColors();
    const watchListAssetsMap = useWatchListAssetsCache();
    let assets;
    if (isEditFlow && id) {
        assets = watchListAssetsMap[id] || [];
    }
    const [name, setName] = useState(isEditFlow ? createParams.watchList.name : '');
    const [emoji, setEmoji] = useState(isEditFlow ? createParams.watchList.emoji : '');
    const [suggestedData, setSuggestedData] = useState(undefined);
    const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
    const textInputRef = useRef(null);
    const btnText = isEditFlow ? 'Save' : 'Continue';
    const [loading, createWatchList, , deleteWatchList, updateWatchlist] = useWatchList();
    const watchlists = useWatchlistCache();
    useLayoutEffect(() => {
        if (isEditFlow && id) {
            navigation.setOptions({
                headerRight: () => (<NavigationHeaderRightIconButton onPress={() => {
                        Alert.alert('Delete the list', `Are you sure you want to delete this list?`, [
                            { style: 'cancel', text: 'Cancel' },
                            {
                                text: 'Delete',
                                style: 'destructive',
                                onPress: () => {
                                    navigation.pop(2);
                                    deleteWatchList(id);
                                },
                            },
                        ]);
                    }}>
              <IconTrash colorKey="primary"/>
            </NavigationHeaderRightIconButton>),
            });
        }
    }, [isEditFlow, deleteWatchList, id]);
    useAutoFocusInput(textInputRef);
    const onPressSave = async () => {
        if (!name) {
            Alert.alert(emojify('Hold on :dizzy:'), 'You must enter a name.', [
                {
                    text: 'Ok',
                    onPress: () => {
                        textInputRef.current?.focus();
                    },
                },
            ]);
            return;
        }
        if (!emoji) {
            Alert.alert(emojify('Hold on :dizzy:'), 'You must select an emoji.');
            return;
        }
        Keyboard.dismiss();
        if (isEditFlow) {
            const { watchList } = createParams;
            if (watchList.name === name && watchList.emoji === emoji) {
                navigation.goBack();
                return;
            }
            const success = await updateWatchlist(watchList.id, name, emoji);
            if (success) {
                navigation.goBack();
            }
        }
        else {
            const { symbol, assetId } = createParams;
            const watchList = await createWatchList(name, emoji);
            if (watchList) {
                if (flow === AddToWatchListFlow.AddToWatchListFromInvest) {
                    navigation.replace('InvestWatchList', { id: watchList.id });
                }
                else if (symbol && assetId) {
                    navigation.pop();
                    AddToWatchListModal.show(watchlists.length, assetId);
                }
                else {
                    // Doesnt get to here
                    navigation.popToTop();
                }
            }
        }
        rateApp();
    };
    const handleSuggestionPress = useCallback((name, emoji) => {
        Keyboard.dismiss();
        setName(name);
        setEmoji(emoji || '');
        setSuggestedData({
            name,
            emoji: emoji || '',
        });
    }, []);
    const onEmojiModalClose = useCallback(() => {
        setIsEmojiPickerOpen(false);
    }, []);
    const onSelectEmoji = useCallback((emoji) => {
        setEmoji(emoji);
        onEmojiModalClose();
    }, [onEmojiModalClose]);
    const onPressEmoji = useCallback(() => {
        Keyboard.dismiss();
        EmojiPickerModal.show(emoji, onSelectEmoji, onEmojiModalClose);
        setIsEmojiPickerOpen(true);
    }, [emoji, onEmojiModalClose, onSelectEmoji]);
    const nameInput = (<SettingsItemInput description="Name" icon={<IconText gradientColors={colors.gradients.darkPink.colors}/>} value={name} onChangeText={setName} placeholder="Choose.." iconSize={44} returnKeyType="done"/>);
    const emojiInput = (<SettingsItemNavigate iconSize={44} title="Emoji" onPress={onPressEmoji} isSelected={isEmojiPickerOpen} icon={emoji ? <EmojiText fontSize={20}>{emoji}</EmojiText> : <IconEmma />}/>);
    const renderItem = useCallback(({ item }) => {
        if (item.state === AssetStateCache.REMOVING) {
            return null;
        }
        return <EditWatchListStockItem data={item} watchListId={id || ''}/>;
    }, [id]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT + rem(16), paddingHorizontalStyle);
    return (<AppView type="secondary" withFrame={false}>
        <AnimatedKeyboardAwareScrollView scrollEventThrottle={16} showsVerticalScrollIndicator={false} onScroll={scrollHandler} contentContainerStyle={paddingBottom} keyboardShouldPersistTaps="handled">
          {isEditFlow ? (<>
              <CardView style={styles.cardContainer}>
                {nameInput}
                {emojiInput}
              </CardView>
              {assets && !!assets.length ? <SectionHeader title="Stocks"/> : null}
              <FlatList data={assets || []} scrollEnabled={false} renderItem={renderItem} initialNumToRender={20} scrollEventThrottle={1} keyboardDismissMode="on-drag" keyExtractor={extractKeyFromKey('symbol')} contentContainerStyle={[styles.contentContainerStyles]}/>
            </>) : (<>
              <CardView>{nameInput}</CardView>
              <CardView noMarginTop noMarginBottom>
                {emojiInput}
              </CardView>
              <WatchlistSuggestions name={name} handleSuggestionPress={handleSuggestionPress} suggestedData={suggestedData}/>
            </>)}
        </AnimatedKeyboardAwareScrollView>
        <Button brand floating title={btnText} onPress={onPressSave} loading={loading}/>
      </AppView>);
}, ({ route }) => ({
    title: route.params.createParams.flow === AddToWatchListFlow.UpdateWatchList ? 'Edit list' : 'Create list',
}));
const styleSet = createStyleSheets((colors) => ({
    cardContainer: {
        marginBottom: 0,
    },
    contentContainerStyles: {
        borderRadius: rem(16),
        marginHorizontal: rem(16),
        backgroundColor: colors.cards.onDark,
    },
}));
export default CreateWatchListScreen;
